"use client";

/**
 * SharedViewContext
 *
 * Provides shared view configuration and data to chart components
 * when rendering in public shared view mode.
 */
import { c as _c } from "react/compiler-runtime";
;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:propstrata-app": true
    });
  } catch (e) {}
}();
import { createContext, type ReactNode, useCallback, useContext } from "react";
import { useSharedViewFilterActions, useVisualizationFilters, type VisualizationFilters } from "@/lib/stores/shared-view-filter-store";
interface SharedViewContextValue {
  /** The shared view slug for API calls */
  slug: string;
  /** Verified viewer email (required for RESTRICTED shared views) */
  viewerEmail?: string;
  /** Session token for RESTRICTED shared views (third-party embed contexts where cookies don't work) */
  sessionToken?: string;
  /** Whether drill-down is allowed in this shared view */
  allowDrillDown: boolean;
  /** The view visualization ID for this specific chart */
  viewVisualizationId: string;
  /** The visualization name */
  visualizationName: string;
  /** Saved visualization config from the view (filters, compareBy, etc.) */
  visualizationConfig?: Record<string, unknown>;
  /** List of visualization names allowed in this shared view (for drill-down filtering) */
  allowedVisualizationNames?: string[];
  /** Leadership mode for leaderboard visualization (GRADE or SCORE) */
  leadershipMode?: string;
}
const SharedViewContext = createContext<SharedViewContextValue | null>(null);
interface SharedViewProviderProps {
  children: ReactNode;
  slug: string;
  viewerEmail?: string;
  /** Session token for RESTRICTED shared views (third-party embed contexts) */
  sessionToken?: string;
  allowDrillDown: boolean;
  viewVisualizationId: string;
  visualizationName: string;
  visualizationConfig?: Record<string, unknown>;
  allowedVisualizationNames?: string[];
  leadershipMode?: string;
}

/**
 * Provider component for shared view context
 */
export function SharedViewProvider(t0) {
  const $ = _c(13);
  const {
    children,
    slug,
    viewerEmail,
    sessionToken,
    allowDrillDown,
    viewVisualizationId,
    visualizationName,
    visualizationConfig,
    allowedVisualizationNames,
    leadershipMode
  } = t0;
  let t1;
  if ($[0] !== allowDrillDown || $[1] !== allowedVisualizationNames || $[2] !== leadershipMode || $[3] !== sessionToken || $[4] !== slug || $[5] !== viewVisualizationId || $[6] !== viewerEmail || $[7] !== visualizationConfig || $[8] !== visualizationName) {
    t1 = {
      allowDrillDown,
      allowedVisualizationNames,
      leadershipMode,
      sessionToken,
      slug,
      viewerEmail,
      viewVisualizationId,
      visualizationConfig,
      visualizationName
    };
    $[0] = allowDrillDown;
    $[1] = allowedVisualizationNames;
    $[2] = leadershipMode;
    $[3] = sessionToken;
    $[4] = slug;
    $[5] = viewVisualizationId;
    $[6] = viewerEmail;
    $[7] = visualizationConfig;
    $[8] = visualizationName;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== children || $[11] !== t1) {
    t2 = <SharedViewContext.Provider value={t1}>{children}</SharedViewContext.Provider>;
    $[10] = children;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}

/**
 * Hook to access shared view context
 * Returns null if not in a shared view context
 */
export function useSharedViewContext() {
  return useContext(SharedViewContext);
}

/**
 * Hook to check if currently rendering in shared view mode
 */
export function useIsSharedView() {
  const context = useContext(SharedViewContext);
  return context !== null;
}

/**
 * Hook to access shared view filter state and actions.
 * Returns null if not in a shared view context.
 *
 * @example
 * ```tsx
 * const filterContext = useSharedViewFilters();
 * if (filterContext) {
 *   const { filters, setFilters } = filterContext;
 *   // Use filters...
 * }
 * ```
 */
export function useSharedViewFilters() {
  const $ = _c(7);
  const context = useSharedViewContext();
  const vizId = context?.viewVisualizationId ?? "";
  const filters = useVisualizationFilters(vizId);
  const {
    setVisualizationFilter
  } = useSharedViewFilterActions();
  let t0;
  if ($[0] !== setVisualizationFilter || $[1] !== vizId) {
    t0 = newFilters => {
      if (vizId) {
        setVisualizationFilter(vizId, newFilters);
      }
    };
    $[0] = setVisualizationFilter;
    $[1] = vizId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const setFilters = t0;
  if (!context) {
    return null;
  }
  let t1;
  if ($[3] !== filters || $[4] !== setFilters || $[5] !== vizId) {
    t1 = {
      filters,
      setFilters,
      visualizationId: vizId
    };
    $[3] = filters;
    $[4] = setFilters;
    $[5] = vizId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}